import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorBox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@gearbox-protocol/deploy-tools-ui";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { api } from "api";
import ForkBackend from "components/fork-form/ForkBackend";
import { backendSchema } from "components/fork-form/schema";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAsyncFn } from "react-use";
import { kvArrayToRecord, preventSubmitOnEnter } from "utils";
import { z } from "zod";

const forkPinSchema = z.object({
  alias: z.string().regex(/^[0-9a-zA-Z\-_]{3,}$/, {
    message:
      "alias must contain letters, numbers, dashes and underscores, and must contain minimum 3 symbols",
  }),
  backend: backendSchema.optional(),
});

type ForkPinSchema = z.infer<typeof forkPinSchema>;

interface ForkPinDialogProps {
  executionId: string;
  open?: boolean;
  onOpen?: (open: boolean) => void;
}

const ForkPinDialog: FC<ForkPinDialogProps> = props => {
  const { executionId, open, onOpen } = props;
  const navigate = useNavigate();

  const form = useForm<ForkPinSchema>({
    resolver: standardSchemaResolver(forkPinSchema),
    defaultValues: {
      alias: "",
      backend: undefined,
    },
  });

  const [{ loading, error }, pin] = useAsyncFn(
    async (data: ForkPinSchema) => {
      await api.makePersistent({
        executionId,
        alias: data.alias,
        backend: data.backend
          ? { ...data.backend, env: kvArrayToRecord(data.backend.env) }
          : undefined,
      });
      // force reload page to reload forks list
      navigate(0);
    },
    [executionId, navigate],
  );

  const submit = form.handleSubmit(values => pin(values));

  return (
    <Dialog modal open={open} onOpenChange={onOpen}>
      <DialogContent className="sm:max-w-[425px] max-h-[calc(100vh-64px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pin fork 📌</DialogTitle>
          <DialogDescription>
            Pinned fork will automatically restart on timeout and when the
            service is restarted
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={submit}
            onKeyDown={preventSubmitOnEnter}
            className="space-y-2"
          >
            <FormField
              control={form.control}
              name="alias"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row items-center gap-2">
                    <FormLabel>Alias</FormLabel>
                    <FormControl>
                      <Input id="alias" {...field} />
                    </FormControl>
                  </div>
                  <FormMessage className="break-words" />
                </FormItem>
              )}
            />

            <ForkBackend control={form.control} compact />

            {!!error && <ErrorBox error={error} />}

            <DialogFooter>
              <Button type="submit" disabled={loading} loading={loading}>
                Pin fork
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ForkPinDialog;
